import {useContext, useEffect, useReducer, useRef} from 'react';
import importedMessages from '@lang/messages';
import {notify} from "@contexts/ContextNotificationProvider";
import HelperAjax from "@helpers/HelperAjax";
import {ContextDataStore} from "@helpers/HelperGlobalDataStore";
/**
 * CSS
 */
import '@baseStyles/main.scss';

/**
 * Helper class (Value Object) for passing parameters and callback functions to the ajaxCall method.
 */
export class AjaxParam {
	callbackSuccess = null;		// Callback on success.
	callbackError = null;		// Callback on error.
	isLoading = true;			// Flag whether a request is in progress.
	messageSuccess = '';		// Success message.
	messageError = '';			// Error message.
	cypressParamString = '';
}

/**
 * Array of 'eventConsume' callbacks optionally defined by some page components. If a
 * component defines such a callback, it is invoked by the eventRaise function.
 * Used as a communication mechanism between the different components on the page,
 * via a publish/subscribe pattern instead of passing callbacks through props.
 *
 * NOTE: This array is also shared with the legacy BaseComponent class (see
 * @components/base/BaseComponent/BaseComponent) so that the Pub/Sub mechanism keeps
 * working between the new (src25) and the old (src) codebases.
 */
export const eventsPubSub = [];

/**
 * Core React hook. Every src25 component (controls, contexts and pages) uses this
 * instead of the former `BaseComponent` base class.
 *
 * Returns a `core` object (base component instance) that mimics the old `this`:
 *  - core.state              - the current state (always up to date via a getter)
 *  - core.setState(s, cb)    - state update + write to the global ContextDataStore
 *  - core.ajaxCall(...)      - ajax requests to the server
 *  - core.reload(...)        - reload the component via AJAX
 *  - core.eventRaise(...)    - raise an event (Pub/Sub pattern)
 *  - core.getLink / navigate / getPageTabId / rootPage / getComponentState / componentValidate
 *  - core.context / messages / componentName / componentToken / mounted / loading / props
 *
 * @param {Object}   [options]
 * @param {Object}   [options.props]         - component props (for pages: {data: SHVO.pageState})
 * @param {?string}  [options.name]          - component name (for pages, e.g. 'PageHosting')
 * @param {?string}  [options.token]         - componentToken (contexts pass it directly)
 * @param {?Object}  [options.initialState]  - initial state (contexts pass the object with methods)
 * @param {boolean}  [options.isPage]        - whether this is a top-level page (sets SHVO.pageComponent)
 * @param {?Function}[options.eventConsume]  - (core, eventName, eventData) => void
 * @return {Object} core
 */
export function useSHCore(options = {}) {

	const {
		props = {},
		name = null,
		token = null,
		initialState = null,
		isPage = false,
		eventConsume = null,
	} = options;

	const context = useContext(ContextDataStore);
	const [, forceRerender] = useReducer((c) => (c + 1) % Number.MAX_SAFE_INTEGER, 0);
	const coreRef = useRef(null);
	const pendingCallbacks = useRef([]);

	/**
	 * One-time "construction" of the instance object. Mimics the old constructor.
	 */
	if (coreRef.current === null) {

		const core = {};

		let stateObj = {};
		let resolvedName = name;
		let data = null;

		if (name && context) {
			context.bindObj(name, core);
			data = context.get(name);
		}

		if (!data) {
			data = props.data;
		}

		if (initialState) {
			stateObj = initialState;
			if (data && !resolvedName) {
				resolvedName = data.componentName;
			}
		} else if (data) {
			if (!resolvedName) {
				resolvedName = data.componentName;
			}
			stateObj = data;
		}

		core.componentName = resolvedName || '';
		core.componentToken = token || (data && data.componentToken) || '';
		core.componentVisible = true;
		core.loading = props.isLazy;
		core.mounted = false;
		core.messages = importedMessages[SHVO.pageLang];
		core.context = context;
		core.props = props;

		/**
		 * core.state always returns the current state object.
		 */
		Object.defineProperty(core, 'state', {
			get() {
				return stateObj;
			},
			enumerable: true,
		});

		/**
		 * Forced re-render (also used by the global store via bindObj/forceUpdate).
		 */
		core.forceUpdate = () => {
			forceRerender();
		};

		/**
		 * State update + write to the global ContextDataStore.
		 * Supports both an object and an updater function (prev) => partial, plus a callback.
		 */
		core.setState = (update, callback) => {
			const partial = typeof update === 'function' ? update(stateObj) : update;
			stateObj = {...stateObj, ...partial};
			if (core.componentName) {
				context.set(core.componentName, stateObj);
			}
			if (typeof callback === 'function') {
				pendingCallbacks.current.push(callback);
			}
			forceRerender();
		};

		core.getComponentState = () => stateObj;

		core.componentValidate = () => true;

		core.getPageTabId = () => {
			if (typeof (sessionStorage) === 'object') {
				let key = 'BaseComponentTabId';
				let tabId = sessionStorage.getItem(key);
				if (tabId === null) {
					tabId = Math.random().toString(36).substr(2, 10);
					sessionStorage.setItem(key, tabId);
				}
				return tabId;
			}
		};

		core.getLink = (pageURL, actionId = '', useTabId = false) => {
			if (actionId !== '') {
				actionId = '/a' + actionId;
			}
			let tabId = '';
			if (useTabId) {
				tabId = core.getPageTabId();
				if (tabId !== '') {
					tabId = '/t' + tabId;
				}
			}
			return '/' + pageURL + actionId + tabId;
		};

		core.rootPage = () => SHVO.pageComponent;

		core.navigate = (url) => {
			if ((url !== '') && (url !== undefined)) {
				window.location = url;
			}
		};

		/**
		 * Raises an event to all registered eventConsume callbacks (Pub/Sub).
		 */
		core.eventRaise = (eventName, eventData) => {
			eventsPubSub.forEach((func) => {
				func(eventName, eventData);
			});
		};

		/**
		 * Reload the component (AJAX ActionGetDefaultState).
		 */
		core.reload = (actionObject, callback = null, showLoading = true) => {
			const ajaxParam = new AjaxParam();
			ajaxParam.callbackSuccess = (respData) => {
				core.setState(respData);
				if (typeof callback === 'function') {
					callback(respData);
				}
			};
			ajaxParam.isLoading = showLoading;
			ajaxParam.cypressParamString = '?actionReload' + core.componentName;
			core.ajaxCall('ActionGetDefaultState', actionObject, ajaxParam);
		};

		/**
		 * Main AJAX method. ALL requests are POST. See the old BaseComponent for details.
		 */
		core.ajaxCall = (actionName, actionData, ajaxParam = null) => {

			if (ajaxParam !== null) {
				let isValid = (typeof ajaxParam == "object") && ajaxParam.hasOwnProperty('isLoading');
				if (!isValid) {
					throw "ajaxParam must be an instance of 'AjaxParam'";
				}
			} else {
				ajaxParam = new AjaxParam();
			}

			let comName = core.componentName;
			let comCSFR = core.componentToken;
			let comPTId = core.getPageTabId();

			let callbackSuccess = ajaxParam.callbackSuccess;
			let callbackError = ajaxParam.callbackError;

			let nowTS = (parseInt(Date.now() / 1000) + SHVO.pageTsDelta).toString(23);
			nowTS = Math.random().toString(8).substr(2, 10) + nowTS.split('').reverse().join('');

			core.loading = ajaxParam.isLoading;

			if (core.loading) {
				core.forceUpdate();
			}

			let body = {
				action_ptid: comPTId,
				action_name: actionName,
				action_data: actionData,
				component_name: comName,
				component_token_csfr: comCSFR,
				component_token_dtsc: nowTS,
				component_url: SHVO.pageURL,
				page_project: SHVO.pageProject,
			};

			let url = SHVO.pageAGW;
			if (window.cypressTest === true) {
				url = url + ajaxParam.cypressParamString;
			}

			HelperAjax.fetch('POST', url, JSON.stringify(body), {
				'x-token-dtsc': nowTS,
				'x-token-csfr': comCSFR,
				'x-http-referer': window.location.href,
				'Content-Type': 'application/json; charset=utf-8'
			}).then(response => {
				let code = response.respCode;
				let data = response.respBody;

				if (code == '205') {
					location.reload(true);
					return;
				}

				if (!data) {
					return;
				}
				try {
					data = JSON.parse(data);

					const {
						componentNewURL,
						componentErrors
					} = data;

					if (componentNewURL) {
						core.navigate(componentNewURL);
						return;
					}
					core.loading = false;

					if (data.componentFlat) {
						core.context.init(data.componentFlat);
					}

					let hasErrors = ((typeof componentErrors === 'object') && (componentErrors.length > 0));

					if (hasErrors) {
						if (typeof callbackError === "function") {
							callbackError(data);
							return;
						} else {
							const errorMessage = componentErrors[0];
							notify.error(core.messages[errorMessage] || errorMessage);
						}
					} else {
						if (typeof callbackSuccess === "function") {
							callbackSuccess(data);
							return;
						}
					}

					core.setState(data);

				} catch (error) {
					console.error(error);
				}

			}).catch(function (error) {
				console.error(error);
			});
		};

		/**
		 * Register the eventConsume callback (Pub/Sub).
		 */
		if (typeof eventConsume === 'function') {
			core._eventConsume = (eventName, eventData) => eventConsume(core, eventName, eventData);
			eventsPubSub.push(core._eventConsume);
		}

		/**
		 * Keep a reference to the page (top-level component).
		 */
		if (isPage) {
			SHVO.pageComponent = core;
		} else if (!SHVO.pageComponent) {
			SHVO.pageComponent = core;
		}

		coreRef.current = core;
	}

	const core = coreRef.current;
	core.props = props;

	/**
	 * Runs the callbacks passed to setState after commit (analog of the setState callback).
	 */
	useEffect(() => {
		if (pendingCallbacks.current.length) {
			const cbs = pendingCallbacks.current;
			pendingCallbacks.current = [];
			cbs.forEach((cb) => cb());
		}
	});

	/**
	 * Analog of componentDidMount / componentWillUnmount.
	 */
	useEffect(() => {
		core.mounted = true;

		if (props.data) {
			const {
				componentToken,
				componentName
			} = props.data;

			if (componentToken) {
				core.componentToken = componentToken;
				core.componentName = componentName;
			}

			if (props.isLazy == '1') {
				const ajaxParam = new AjaxParam();
				ajaxParam.callbackSuccess = (respData) => {
					if (SHVO.pageComponent.componentName === core.componentName) {
						SHVO.pageState = respData;
					}
					if (respData.componentToken) {
						core.componentToken = respData.componentToken;
					}
					core.setState(respData);
					core.eventRaise('LazyLoadingComplete', respData);
				};
				ajaxParam.cypressParamString = '?LazyLoading-' + componentName;
				core.ajaxCall('ActionGetDefaultState', {}, ajaxParam);
			}
		}

		return () => {
			core.mounted = false;
			if (core.componentName) {
				context.bindObj(core.componentName, null);
			}
			if (core._eventConsume) {
				const idx = eventsPubSub.indexOf(core._eventConsume);
				if (idx !== -1) {
					eventsPubSub.splice(idx, 1);
				}
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Analog of getDerivedStateFromProps - redirect when componentNewURL is provided.
	 */
	useEffect(() => {
		if (props.data) {
			const {
				componentNewURL,
				componentInital,
			} = props.data;

			if (componentInital) {
				return;
			}
			if ((componentNewURL !== '') && (componentNewURL !== undefined)) {
				window.location = componentNewURL;
			}
		}
	}, [props.data]);

	return core;
}
